/**
 * Password Security Validation Utilities
 * Implements strong password requirements for enhanced security
 */

// Common passwords to reject (top 100 most common)
const COMMON_PASSWORDS = [
  'password', 'password123', '123456', '12345678', 'qwerty', 'abc123',
  'monkey', '1234567', 'letmein', 'trustno1', 'dragon', 'baseball',
  'iloveyou', 'master', 'sunshine', 'ashley', 'bailey', 'passw0rd',
  'shadow', '123123', '654321', 'superman', 'qazwsx', 'michael',
  'football', 'welcome', 'jesus', 'ninja', 'mustang', 'password1',
  'admin', 'admin123', 'root', 'toor', 'pass', 'test', 'guest',
  '123456789', '1234567890', '12345', '123', 'password!', 'Password1',
  'Welcome1', 'Qwerty123', 'Abc123', 'Password123', 'Admin123'
];

/**
 * Validates password strength according to security best practices
 * @param {string} password - The password to validate
 * @returns {Object} - Validation result with success status and detailed feedback
 */
export const validatePassword = (password) => {
  const result = {
    isValid: false,
    errors: [],
    warnings: [],
    strength: 'weak',
    score: 0
  };

  // Check minimum length (12 characters)
  if (password.length < 12) {
    result.errors.push('Password must be at least 12 characters long');
  } else {
    result.score += 20;
  }

  // Check for uppercase letters
  if (!/[A-Z]/.test(password)) {
    result.errors.push('Password must contain at least one uppercase letter (A-Z)');
  } else {
    result.score += 15;
  }

  // Check for lowercase letters
  if (!/[a-z]/.test(password)) {
    result.errors.push('Password must contain at least one lowercase letter (a-z)');
  } else {
    result.score += 15;
  }

  // Check for numbers
  if (!/[0-9]/.test(password)) {
    result.errors.push('Password must contain at least one number (0-9)');
  } else {
    result.score += 15;
  }

  // Check for special characters
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    result.errors.push('Password must contain at least one special character (!@#$%^&* etc.)');
  } else {
    result.score += 15;
  }

  // Check against common passwords (case-insensitive)
  if (COMMON_PASSWORDS.includes(password.toLowerCase())) {
    result.errors.push('This password is too common. Please choose a more unique password');
  }

  // Check for sequential characters (123, abc, etc.)
  if (/(?:abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789)/i.test(password)) {
    result.warnings.push('Avoid using sequential characters (abc, 123, etc.)');
    result.score -= 5;
  }

  // Check for repeated characters (aaa, 111, etc.)
  if (/(.)\1{2,}/.test(password)) {
    result.warnings.push('Avoid repeating the same character multiple times');
    result.score -= 5;
  }

  // Bonus points for length > 16
  if (password.length >= 16) {
    result.score += 10;
    result.warnings.push('Excellent length!');
  }

  // Bonus points for multiple special characters
  const specialCharCount = (password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/g) || []).length;
  if (specialCharCount >= 2) {
    result.score += 5;
  }

  // Bonus points for numbers and letters mixed throughout (not just at end)
  if (/^(?=.*[a-zA-Z].*[0-9])(?=.*[0-9].*[a-zA-Z])/.test(password)) {
    result.score += 5;
  }

  // Calculate strength based on score
  result.score = Math.min(100, Math.max(0, result.score));

  if (result.score >= 80) {
    result.strength = 'strong';
  } else if (result.score >= 60) {
    result.strength = 'medium';
  } else {
    result.strength = 'weak';
  }

  // Overall validation
  result.isValid = result.errors.length === 0;

  return result;
};

/**
 * Gets a color for password strength indicator
 * @param {string} strength - The strength level (weak, medium, strong)
 * @returns {string} - Color code for UI display
 */
export const getStrengthColor = (strength) => {
  switch (strength) {
    case 'strong':
      return '#4caf50'; // green
    case 'medium':
      return '#ff9800'; // orange
    case 'weak':
      return '#f44336'; // red
    default:
      return '#9e9e9e'; // grey
  }
};

/**
 * Validates email format
 * @param {string} email - The email to validate
 * @returns {boolean} - True if valid email format
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Sanitizes user input to prevent injection attacks
 * @param {string} input - The input to sanitize
 * @returns {string} - Sanitized input
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;

  // Remove any HTML tags
  let sanitized = input.replace(/<[^>]*>/g, '');

  // Remove potential SQL injection patterns (basic)
  sanitized = sanitized.replace(/(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE|SCRIPT|UNION)\b)/gi, '');

  // Trim whitespace
  sanitized = sanitized.trim();

  return sanitized;
};

/**
 * Validates coordinate inputs
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {Object} - Validation result
 */
export const validateCoordinates = (lat, lon) => {
  const result = { isValid: true, error: '' };

  if (typeof lat !== 'number' || typeof lon !== 'number') {
    result.isValid = false;
    result.error = 'Coordinates must be numbers';
    return result;
  }

  if (lat < -90 || lat > 90) {
    result.isValid = false;
    result.error = 'Latitude must be between -90 and 90';
    return result;
  }

  if (lon < -180 || lon > 180) {
    result.isValid = false;
    result.error = 'Longitude must be between -180 and 180';
    return result;
  }

  return result;
};

export default {
  validatePassword,
  getStrengthColor,
  validateEmail,
  sanitizeInput,
  validateCoordinates
};
